import { ReactNode, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import SignInModal from "@/components/auth/SignInModal";
import { Loader2 } from "lucide-react";

interface Props {
  children: ReactNode;
}

const AdminGuard = ({ children }: Props) => {
  const { isAdmin, loading, user } = useIsAdmin();
  const location = useLocation();
  const [signInOpen, setSignInOpen] = useState(true);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4 px-6">
        <h1 className="text-2xl font-serif text-foreground">Admin sign-in required</h1>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Sign in with your admin email to access the blog admin.
        </p>
        <SignInModal open={signInOpen} onOpenChange={setSignInOpen} />
        {!signInOpen && (
          <button
            onClick={() => setSignInOpen(true)}
            className="text-sm underline text-foreground"
          >
            Open sign-in
          </button>
        )}
      </div>
    );
  }

  const ALLOWED_ADMIN_EMAIL = "support@tonyknows.co.uk";
  const emailAllowed =
    (user.email ?? "").trim().toLowerCase() === ALLOWED_ADMIN_EMAIL;

  if (!isAdmin || !emailAllowed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-3 px-6">
        <h1 className="text-2xl font-serif text-foreground">Not authorised</h1>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          Your account ({user.email}) doesn't have admin access to the blog.
        </p>
        <Navigate to="/" replace state={{ from: location }} />
      </div>
    );
  }

  return (
    <div data-clarity-mask="true" data-clarity-unmask="false">
      {children}
    </div>
  );
};

export default AdminGuard;
